import { useEffect } from 'react';
import moment from 'moment';
import { PagedQueryParams } from '../ServerSideGrid';

interface AnalyticsEffectsProps {
  reportType: string;
  initQueryParams: any;
  queryState: any;
  setQueryState: (val: any) => void;
  setAndOr: (val: string) => void;
  setSubmitShowContent: (val: boolean) => void;
  setDateKey: (cb: (prev: number) => number) => void;
  setDisableBtn: (val: boolean) => void;
  selectedMap: any;
  checkIfErrorExist: (
    reportType: string,
    queryState: PagedQueryParams,
    selectedMap: any,
    setDisableBtn: (val: boolean) => void
  ) => void;
  isAnalyticsPage: boolean;
}

// Validations
const validateGatewayReport = (queryState: PagedQueryParams, selectedMap: any) => {
  return !!selectedMap?.value && queryState.dateFilters.start && queryState.dateFilters.end;
};
const validateTriggerReport = (queryState: PagedQueryParams) => {
  return queryState.dateFilters.start && queryState.dateFilters.end;
};
const validateDefault = (queryState: PagedQueryParams) => {
  return queryState.dateFilters.start && queryState.dateFilters.end;
};

// Error check
export const checkIfErrorExist = (
  reportType: string,
  queryState: PagedQueryParams,
  selectedMap: any,
  setDisableBtn: (val: boolean) => void
) => {
  let isValid = false;

  switch (reportType) {
    case 'gateway-network-uptime':
      isValid = validateGatewayReport(queryState, selectedMap);
      break;
    case 'trigger-resolution':
      isValid = validateTriggerReport(queryState);
      break;
    default:
      isValid = validateDefault(queryState);
      break;
  }

  const muiErrors = document?.getElementsByClassName('Mui-error');
  const muiHelperText = document?.getElementsByClassName('MuiFormHelperText-contained');

  if (!isValid || muiErrors?.length > 0 || muiHelperText?.length > 0) {
    setDisableBtn(true);
  } else {
    setDisableBtn(false);
  }
};

// Date change handlers
export const onChangeStartDate = (
  e: any,
  queryState: PagedQueryParams,
  setQueryState: (val: PagedQueryParams) => void
) => {
  if (e === undefined) {
    setQueryState({
      ...queryState,
      dateFilters: {
        start: e,
        end: queryState.dateFilters.end,
      },
    });
  } else {
    const now = moment();
    const selectedStartDate = moment(e);
    let endDate;

    if (selectedStartDate.isSame(now, 'day')) {
      endDate = now.clone().format('YYYY-MM-DDTHH:mm:ss');
    } else {
      endDate =
        queryState.dateFilters.end === undefined
          ? selectedStartDate.clone().add(1, 'day').format('YYYY-MM-DDTHH:mm:ss')
          : queryState.dateFilters.end;
    }

    setQueryState({
      ...queryState,
      dateFilters: {
        start: selectedStartDate.format('YYYY-MM-DDTHH:mm:ss'),
        end: endDate,
      },
    });
  }
};

export const onChangeEndDate = (
  e: any,
  queryState: PagedQueryParams,
  setQueryState: (val: PagedQueryParams) => void
) => {
  if (e === undefined) {
    setQueryState({
      ...queryState,
      dateFilters: {
        start: queryState.dateFilters.start,
        end: e,
      },
    });
  } else {
    const startDate =
      queryState.dateFilters.start ??
      moment(e).subtract(1, 'day').format('YYYY-MM-DDTHH:mm:ss');

    setQueryState({
      ...queryState,
      dateFilters: {
        start: startDate,
        end: e,
      },
    });
  }
};

// Submit Button Handler
export const handleSubmitReport = (
  queryState: PagedQueryParams,
  setQueryState: (val: PagedQueryParams) => void,
  debounceFetchData: Function,
  assetFilters: string[],
  andOr: string,
  props: any,
  setSubmitShowContent: (val: boolean) => void
) => {
  queryState.pageNumber = 1;
  setQueryState(queryState);
  debounceFetchData(queryState, assetFilters, andOr, props, false);
  setSubmitShowContent(true);
};

// Analytics Map Change Handler for GNUR
export const handleMapChange = (
  mapId: string,
  mapName: string,
  props: any,
  setSelectedMap: (val: any) => void,
  setQueryState: (updater: any) => void,
  checkIfErrorExist: (
    reportType: string,
    queryState: PagedQueryParams,
    selectedMap: any,
    setDisableBtn: (val: boolean) => void
  ) => void,
  queryState: PagedQueryParams,
  setDisableBtn: (val: boolean) => void
) => {
  setSelectedMap({ value: mapId });

  setQueryState((prevState: PagedQueryParams) => {
    prevState.mapId = mapId;
    return prevState;
  });

  checkIfErrorExist(props.reportType, queryState, { value: mapId }, setDisableBtn);

  if (props.onMapChange) {
    props.onMapChange(mapId, mapName);
  }
};


export function useAnalyticsEffects({
  reportType,
  initQueryParams,
  queryState,
  setQueryState,
  setAndOr,
  setSubmitShowContent,
  setDateKey,
  setDisableBtn,
  selectedMap,
  checkIfErrorExist,
}: AnalyticsEffectsProps) {
  // Effect for handling reportType changes
  useEffect(() => {
    if (!reportType) return;

    setQueryState({
      ...initQueryParams,
      pageNumber: 1,
      pageSize: 20,
    });

    if (reportType === 'gateway-network-uptime') {
      setAndOr('and');
    }

    setSubmitShowContent(false);
    setDateKey((prev) => prev + 1);

    setDisableBtn(reportType !== 'battery');
  }, [reportType]);

  // Effect for running error check on date change
  useEffect(() => {
    if (reportType && reportType !== 'battery') {
      const timeout = setTimeout(() => {
        checkIfErrorExist(reportType, queryState, selectedMap, setDisableBtn);
      }, 100);

      return () => clearTimeout(timeout); // cleanup
    }
  }, [queryState?.dateFilters]);
}
